import axios from 'axios';
import { Site, Document, QueryAnswer, UploadResponse } from '@/types';

const api = axios.create({
  baseURL: '/backend',
  headers: { 'Content-Type': 'application/json' },
  withCredentials: true,
});

// CSRF token stored in memory — fetched from /auth/csrf/
let csrfToken = '';

api.interceptors.request.use((config) => {
  if (csrfToken && config.method && !['get', 'head', 'options'].includes(config.method)) {
    config.headers['X-CSRFToken'] = csrfToken;
  }
  return config;
});

// ── Sites ──────────────────────────────────────────────────────────────────
export const getSite = (siteNumber: string) =>
  api.get<Site>(`/sites/${siteNumber}/`).then(r => r.data);

export const listSites = () =>
  api.get<Site[]>('/sites/').then(r => r.data);

export const createSite = (siteNumber: string, name?: string) =>
  api.post<Site>('/sites/', { site_number: siteNumber, name: name || '' }).then(r => r.data);

export const updateSiteName = (siteNumber: string, name: string) =>
  api.patch<Site>(`/sites/${siteNumber}/update/`, { name }).then(r => r.data);

// ── Documents ──────────────────────────────────────────────────────────────
export const listDocuments = (siteNumber: string) =>
  api.get<Document[]>(`/sites/${siteNumber}/documents/`).then(r => r.data);

export const uploadDocuments = (siteNumber: string, files: File[]) => {
  const form = new FormData();
  files.forEach(f => form.append('files', f));
  return api.post<UploadResponse>(
    `/sites/${siteNumber}/documents/upload/`,
    form,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  ).then(r => r.data);
};

export const deleteDocument = (siteNumber: string, docId: string) =>
  api.delete(`/sites/${siteNumber}/documents/${docId}/delete/`).then(r => r.data);

export const reprocessDocument = (siteNumber: string, docId: string) =>
  api.post<Document>(`/sites/${siteNumber}/documents/${docId}/reprocess/`).then(r => r.data);

export const getDocumentStatus = (siteNumber: string, docId: string) =>
  api.get<Document>(`/sites/${siteNumber}/documents/${docId}/`).then(r => r.data);

export const getDocumentDetail = (siteNumber: string, docId: string) =>
  api.get(`/sites/${siteNumber}/documents/${docId}/detail/`).then(r => r.data);

// ── Query ──────────────────────────────────────────────────────────────────
export const queryDocuments = (siteNumber: string, query: string, sessionId?: string) =>
  api.post<QueryAnswer>(`/sites/${siteNumber}/query/`, { query, session_id: sessionId || '' }).then(r => r.data);

export const getRecentChats = (siteNumber: string, limit = 10) =>
  api.get(`/sites/${siteNumber}/chats/`, { params: { limit: String(limit) } }).then(r => r.data);

// ── Feedback ───────────────────────────────────────────────────────────────
export interface FeedbackPayload {
  session_id: string;
  query: string;
  returned_value: string;
  correct_value: string;
  feedback_type: 'wrong_value' | 'wrong_unit' | 'wrong_stream' | 'wrong_component' | 'not_found' | 'other';
  feedback_note: string;
}

export const submitFeedback = (siteNumber: string, payload: FeedbackPayload) =>
  api.post(`/sites/${siteNumber}/feedback/`, payload).then(r => r.data);

export const listFeedback = (siteNumber?: string, limit = 20, offset = 0) => {
  const params: Record<string, string> = { limit: String(limit), offset: String(offset) };
  if (siteNumber) params.site_number = siteNumber;
  return api.get('/feedback/', { params }).then(r => r.data);
};

// ── Tables & Cells ─────────────────────────────────────────────────────────
export const listTables = (siteNumber?: string, limit = 20, offset = 0) => {
  const params: Record<string, string> = { limit: String(limit), offset: String(offset) };
  if (siteNumber) params.site_number = siteNumber;
  return api.get('/tables/', { params }).then(r => r.data);
};

export const listCells = (params: { site_number?: string; table_id?: string; search?: string; limit?: number; offset?: number }) => {
  const q: Record<string, string> = { limit: String(params.limit || 20), offset: String(params.offset || 0) };
  if (params.site_number) q.site_number = params.site_number;
  if (params.table_id) q.table_id = params.table_id;
  if (params.search) q.search = params.search;
  return api.get('/cells/', { params: q }).then(r => r.data);
};

// ── Audit Logs ─────────────────────────────────────────────────────────────
export const listAuditLogs = (siteNumber?: string, limit = 20, offset = 0) => {
  const params: Record<string, string> = { limit: String(limit), offset: String(offset) };
  if (siteNumber) params.site_number = siteNumber;
  return api.get('/logs/', { params }).then(r => r.data);
};

// ── Vector Explorer ────────────────────────────────────────────────────────
export const exploreVectors = (siteNumber?: string, filename?: string, limit = 20, offset = 0) => {
  const params: Record<string, string> = { limit: String(limit), offset: String(offset) };
  if (siteNumber) params.site_number = siteNumber;
  if (filename) params.filename = filename;
  return api.get('/vectors/', { params }).then(r => r.data);
};

// ── Cost Tracking ──────────────────────────────────────────────────────────
export const getCostSummary = (params: {
  site_number?: string; doc_id?: string; session_id?: string; days?: number
} = {}) => {
  const q: Record<string, string> = {};
  if (params.site_number) q.site_number = params.site_number;
  if (params.doc_id) q.doc_id = params.doc_id;
  if (params.session_id) q.session_id = params.session_id;
  if (params.days) q.days = String(params.days);
  return api.get('/costs/', { params: q }).then(r => r.data);
};

// ── Dashboard ──────────────────────────────────────────────────────────────
export const getDashboardStats = () =>
  api.get('/dashboard/').then(r => r.data);

// ── Auth ────────────────────────────────────────────────────────────────────
export const getCsrfToken = async () => {
  const data = await api.get('/auth/csrf/').then(r => r.data);
  csrfToken = data.csrfToken;
  return data;
};

export const login = async (username: string, password: string) => {
  await getCsrfToken();
  return api.post('/auth/login/', { username, password }).then(r => r.data);
};

export const getMe = () =>
  api.get('/auth/me/').then(r => r.data);

export const logout = () =>
  api.post('/auth/logout/').then(r => r.data);

export default api;
